import { MagnifyingGlassIcon, CalendarIcon, ArrowSquareOutIcon, UploadSimpleIcon, CaretUpIcon, CaretDownIcon, CheckCircleIcon, XCircleIcon } from '@phosphor-icons/react';
import { usePayments } from '../hooks/usePayments';
import { useHaptic } from '../../../hooks/useHaptic';
import { useScrollRestoration } from '../../../hooks/useScrollRestoration';
import { useNavigate } from 'react-router-dom';
import { TableRowSkeleton } from '../../../components/ui/Skeleton';
import { ErrorBoundary } from '../../../components/ui/ErrorBoundary';
import { ImportDataModal } from './ImportDataModal';
import { PageHeader } from '../../../components/ui/PageHeader';
import { Pagination } from '../../../components/ui/Pagination';
import { useState } from 'react';
import { Button } from '../../../components/ui/Button';

/** Safely convert any value to string */
const safeStr = (val: unknown, fallback = 'N/A'): string => {
  if (val === null || val === undefined) return fallback;
  if (typeof val === 'object') return JSON.stringify(val);
  const s = String(val).trim();
  return s === '' ? fallback : s;
};

interface PaymentsViewProps {
  onBack: () => void;
}

export function PaymentsView({ onBack }: PaymentsViewProps) {
  const { lightTap, mediumTap } = useHaptic();
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const {
    searchTerm,
    startDate,
    endDate,
    payments,
    loading,
    error,
    page,
    setPage,
    totalPages,
    totalItems,
    sortBy,
    sortOrder,
    handleSearchChange,
    handleStartDateChange,
    handleEndDateChange,
    handleSort,
  } = usePayments(50);

  useScrollRestoration(!loading && payments.length > 0);

  const navigate = useNavigate();

  const handleRowClick = (donorId: string) => {
    mediumTap();
    navigate(`/alumni/${donorId}`);
  };

  const renderSortIcon = (field: string) => {
    if (sortBy !== field) return null;
    return sortOrder === 1
      ? <CaretUpIcon size={11} weight="bold" className="shrink-0" />
      : <CaretDownIcon size={11} weight="bold" className="shrink-0" />;
  };

  return (
    <ErrorBoundary>
      <div className="space-y-6 font-sans">
        {/* Header */}
        <PageHeader
          breadcrumb={[
            { label: 'Dashboard', onClick: onBack },
            { label: 'Alumni Payments' },
          ]}
          title="Alumni Payments"
          subtitle="View and track all payments and donations made by alumni."
          actions={
            <Button
              onClick={() => { lightTap(); setIsImportModalOpen(true); }}
              className="flex items-center gap-1.5 shadow-sm"
            >
              <UploadSimpleIcon size={14} weight="bold" />
              Import Data
            </Button>
          }
        />

        {/* Filter bar + Table card */}
        <div className="bg-white border border-slate-200/60 rounded-2xl shadow-sm">
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-3 items-end p-5 border-b border-slate-100">
            {/* Search */}
            <div className="flex-1 relative min-w-0">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                <MagnifyingGlassIcon size={15} weight="bold" />
              </div>
              <input
                type="text"
                placeholder="Search by Payment ID, Donor ID, Name, Project..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:border-accent focus:ring-4 focus:ring-accent/10 focus:bg-white transition-smooth"
              />
            </div>

            {/* Date range */}
            <div className="flex gap-3 shrink-0">
              {[
                { label: 'From', value: startDate, onChange: handleStartDateChange },
                { label: 'To', value: endDate, onChange: handleEndDateChange },
              ].map(({ label, value, onChange }) => (
                <div key={label} className="flex flex-col gap-1">
                  <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{label}</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                      className="pl-8 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:border-accent focus:ring-4 focus:ring-accent/10 focus:bg-white transition-smooth cursor-pointer"
                    />
                    <CalendarIcon size={13} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="text-slate-400 text-[10px] font-bold uppercase tracking-wider border-b border-slate-100">
                  {[
                    { label: 'Date', field: 'date', className: 'pl-5 pb-3 w-[7%]' },
                    { label: 'Close Date', field: 'closure_date', className: 'pb-3 w-[7%]' },
                    { label: 'Donated By', field: 'donor_name', className: 'pb-3 w-[12%]' },
                    { label: 'Project', field: 'project', className: 'pb-3 w-[12%]' },
                    { label: 'Amount', field: 'amount', className: 'pb-3 pr-2 w-[8%]' },
                    { label: 'Stage / Prob', field: 'stage', className: 'pb-3 w-[9%]' },
                    { label: 'Type / Cat', field: 'type', className: 'pb-3 w-[10%]' },
                    { label: 'IDs', field: 'donor_id', className: 'pb-3 w-[9%]' },
                    { label: 'Paid', field: 'paid', className: 'pb-3 w-[5%]' },
                    { label: 'Through', field: 'donation_through', className: 'pb-3 w-[8%]' },
                    { label: 'Link', field: 'salesforce_object', className: 'pb-3 w-[6%]' },
                  ].map(({ label, field, className }) => (
                    <th
                      key={field}
                      className={`${className} cursor-pointer hover:text-slate-700 transition-colors`}
                      onClick={() => handleSort(field)}
                    >
                      <div className="flex items-center gap-1">
                        {label} {renderSortIcon(field)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50 text-xs font-sans">
                {loading ? (
                  <TableRowSkeleton cols={11} rows={8} />
                ) : error ? (
                  <tr>
                    <td colSpan={11} className="py-10 text-center text-rose-500 font-semibold">{error}</td>
                  </tr>
                ) : payments.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="py-10 text-center text-slate-400 font-medium">No matching payments found.</td>
                  </tr>
                ) : (
                  payments.map((p, idx) => (
                    <tr
                      key={p.payment_id || idx}
                      onClick={() => handleRowClick(p.donor_id)}
                      className="hover:bg-slate-50/80 transition-smooth group cursor-pointer"
                    >
                      <td className="py-4 pl-5 font-semibold text-slate-500">
                        {p.date ? new Date(p.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                      </td>
                      <td className="py-4 font-semibold text-slate-500">
                        {p.closure_date ? ((p.closure_date as any).$date ? new Date((p.closure_date as any).$date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : (typeof p.closure_date === 'string' && p.closure_date.includes('T') ? new Date(p.closure_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : (typeof p.closure_date === 'string' ? p.closure_date : 'N/A'))) : 'N/A'}
                      </td>
                      <td className="py-4">
                        <p className="font-bold text-slate-750 group-hover:text-accent transition-colors truncate max-w-[140px]">{safeStr(p.donor_name)}</p>
                      </td>
                      <td className="py-4">
                        <p className="font-semibold text-slate-700 leading-snug truncate max-w-[140px]">{safeStr(p.project)}</p>
                      </td>
                      <td className="py-4 pr-2">
                        <span className="font-extrabold text-slate-900 whitespace-nowrap">
                          {p.currency === 'INR' ? '₹' : p.currency === 'USD' ? '$' : `${p.currency || '₹'} `}
                          {Number(p.amount).toLocaleString('en-IN')}
                        </span>
                      </td>
                      <td className="py-4">
                        <p className="font-semibold text-slate-700">{safeStr(p.stage)}</p>
                        {p.probability !== undefined && p.probability !== null && <p className="text-[10px] text-slate-500 font-medium">{p.probability*100}%</p>}
                      </td>
                      <td className="py-4">
                        <p className="font-semibold text-slate-700 truncate max-w-[110px]">{safeStr(p.type)}</p>
                        {(p.donor_category || p.receipt_category) && (
                           <p className="text-[10px] text-slate-500 font-medium truncate max-w-[110px]">
                             {[p.donor_category, p.receipt_category].filter(Boolean).join(' • ')}
                           </p>
                        )}
                      </td>
                      <td className="py-4">
                        <span className="font-mono text-[10px] text-slate-500 font-semibold truncate block max-w-[90px]" title={p.donor_id}>D: {safeStr(p.donor_id)}</span>
                        <span className="font-mono text-[10px] text-slate-500 font-semibold truncate block max-w-[90px]" title={p.payment_id}>P: {safeStr(p.payment_id)}</span>
                      </td>
                      <td className="py-4">
                        {p.paid ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200/60">
                            <CheckCircleIcon size={10} weight="fill" />
                            Yes
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-rose-50 text-rose-600 border border-rose-200/60">
                            <XCircleIcon size={10} weight="fill" />
                            No
                          </span>
                        )}
                      </td>
                      <td className="py-4">
                        <p className="font-semibold text-slate-500 truncate max-w-[110px]">{safeStr(p.donation_through)}</p>
                      </td>
                      <td className="py-4">
                        {p.salesforce_object ? (
                          <a
                            href={p.salesforce_object.startsWith('http') ? p.salesforce_object : `https://${p.salesforce_object}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold text-slate-500 bg-slate-100 hover:bg-accent/10 hover:text-accent rounded-lg transition-smooth border border-slate-200 hover:border-accent/20 uppercase tracking-wider"
                          >
                            Link <ArrowSquareOutIcon size={11} weight="bold" />
                          </a>
                        ) : (
                          <span className="text-slate-300 font-semibold text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && !error && payments.length > 0 && (
            <div className="px-5 pb-5">
              <Pagination
                page={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={50}
                itemLabel="payment"
                onPageChange={(p) => setPage(p)}
              />
            </div>
          )}
        </div>

        <ImportDataModal isOpen={isImportModalOpen} onClose={() => setIsImportModalOpen(false)} />
      </div>
    </ErrorBoundary>
  );
}
